import type {
  ItineraryHotelSource,
  ItineraryLeg,
  ItineraryRoomGroup,
  LegRoomOverrideRoom,
} from '@utazzo/shared';

/**
 * Per-stay room-configuration override (client side).
 *
 * The TBO hotel search occupancy for one stay comes from that stay's leg
 * override when set, otherwise the itinerary's global room config. This is the
 * single place the admin resolves "override-or-global", so the picker, the
 * auto-pick, and the "Change rooms & meals" re-fetch all agree.
 */

export interface StayPaxRoom {
  adults: number;
  children: number;
  childAges: number[];
}

const DEFAULT_CHILD_AGE = 8;

interface DetailLike {
  roomGroups?: ItineraryRoomGroup[];
  legs?: ItineraryLeg[];
  adultsCount?: number;
  childrenCount?: number;
  infantsCount?: number;
  hotelSource?: ItineraryHotelSource;
}

/**
 * The hotel source for a stay: the leg's per-stay override when set, else the
 * itinerary's global `hotelSource`. Decides whether the Days tab opens the Live
 * (TBO) picker or the Preferred (in-house) picker for that city.
 */
export function stayHotelSource(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): ItineraryHotelSource {
  const leg = resolveStayLeg(detail, cityId, date);
  return leg?.hotelSourceOverride ?? detail?.hotelSource ?? 'TBO';
}

/** The leg a stay belongs to (first leg with the given city). */
export function findStayLeg(detail: DetailLike | null, cityId: string | null): ItineraryLeg | null {
  if (!detail || !cityId) return null;
  return (detail.legs ?? []).find((l) => l.cityId === cityId) ?? null;
}

/**
 * The leg a given calendar day belongs to, resolved by the leg's stay dates
 * (`checkIn <= date < checkOut`). Unlike {@link findStayLeg} this is unambiguous
 * when a city repeats in the route (e.g. a return to the same city after a
 * cruise): each stay has its own date window. `date` is a "YYYY-MM-DD" string.
 * Returns null for a day no leg covers (a cruise night, or the final checkout
 * day) or on legacy legs without resolved dates.
 */
export function legForDate<T extends { checkIn: string | null; checkOut: string | null }>(
  legs: T[] | null | undefined,
  date: string | null,
): T | null {
  if (!date || !legs) return null;
  return (
    legs.find((l) => l.checkIn && l.checkOut && date >= l.checkIn && date < l.checkOut) ?? null
  );
}

/**
 * Resolve a stay's leg: by date when given (unambiguous for a city that repeats
 * in the route), else the first leg matching the city. The date wins only when
 * it actually lands inside a leg window, so a date that falls on a cruise night
 * or the final checkout day falls back to the city match.
 */
export function resolveStayLeg(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): ItineraryLeg | null {
  if (date) {
    const byDate = legForDate(detail?.legs, date);
    if (byDate) return byDate;
  }
  return findStayLeg(detail, cityId);
}

/** The room override for a stay's leg, or null when none is set. */
export function stayRoomsOverride(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): LegRoomOverrideRoom[] | null {
  const leg = resolveStayLeg(detail, cityId, date);
  const override = leg?.roomsOverride;
  return override && override.length > 0 ? override : null;
}

function roomsToPax(
  rooms: Array<{ adults: number; children: number; childAges?: number[] }>,
): StayPaxRoom[] {
  return rooms.map((r) => ({
    adults: Math.max(1, r.adults),
    children: r.children,
    childAges: Array.from({ length: r.children }, (_, i) => r.childAges?.[i] ?? DEFAULT_CHILD_AGE),
  }));
}

/**
 * The TBO `paxRooms` to search a stay with: the leg override when set, else the
 * flattened global room config, else a single-room fallback from the pax
 * counts. Mirrors the server's `roomsToPaxRooms` / global fallback exactly.
 */
export function paxRoomsForStay(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): StayPaxRoom[] {
  const override = stayRoomsOverride(detail, cityId, date);
  if (override) return roomsToPax(override);

  const rooms = (detail?.roomGroups ?? []).flatMap((g) => g.rooms);
  if (rooms.length > 0) return roomsToPax(rooms);

  const adults = Math.max(1, detail?.adultsCount ?? 2);
  const children = detail?.childrenCount ?? 0;
  return [
    { adults, children, childAges: Array.from({ length: children }, () => DEFAULT_CHILD_AGE) },
  ];
}

export interface StayRoomFull {
  adults: number;
  children: number;
  childrenNoBed: number;
  infants: number;
}

/**
 * True when `roomsForStayFull` prices exactly the itinerary's global flattened
 * rooms (no per-stay override, and real rooms are configured), so a per-room
 * array lines up with the itinerary's rooms.
 */
export function stayRoomsAreGlobal(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): boolean {
  if (stayRoomsOverride(detail, cityId, date)) return false;
  return (detail?.roomGroups ?? []).some((g) => g.rooms.length > 0);
}

/**
 * Full per-room occupancy for a stay (adults/children/childrenNoBed/infants),
 * used by the in-house (preferred) hotel pricing engine. Leg override when set,
 * else the global room config, else a single-room fallback.
 */
export function roomsForStayFull(
  detail: DetailLike | null,
  cityId: string | null,
  date?: string | null,
): StayRoomFull[] {
  const override = stayRoomsOverride(detail, cityId, date);
  const source = override ?? (detail?.roomGroups ?? []).flatMap((g) => g.rooms);
  if (source.length > 0) {
    return source.map((r) => ({
      adults: Math.max(1, r.adults),
      children: r.children,
      childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
      infants: r.infants ?? 0,
    }));
  }
  return [
    {
      adults: Math.max(1, detail?.adultsCount ?? 2),
      children: detail?.childrenCount ?? 0,
      childrenNoBed: 0,
      infants: detail?.infantsCount ?? 0,
    },
  ];
}
